import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { valuationLabels } from "@/i18n";

interface FinalDetailsSectionProps {
  mileage: string | undefined;
  condition: string | undefined;
  keys: number | undefined;
  horsePower: string | undefined;
  electricDriveRange: number | undefined;
  fuelType: string | undefined;
  onMileageChange: (value: string) => void;
  onConditionChange: (value: string) => void;
  onKeysChange: (value: number) => void;
  onHorsePowerChange: (value: string) => void;
  onElectricDriveRangeChange: (value: string) => void;
  onNext: () => void;
  showCondition?: boolean;
}

export const FinalDetailsSection = ({
  mileage,
  condition,
  keys,
  horsePower,
  electricDriveRange,
  fuelType,
  onMileageChange,
  onConditionChange,
  onKeysChange,
  onHorsePowerChange,
  onElectricDriveRangeChange,
  onNext,
  showCondition = true,
}: FinalDetailsSectionProps) => {
  const CONDITION_KEYS = [
    "excellent",
    "good",
    "normal",
    "poor",
    "renovation",
  ] as const;
  const { language } = useLanguage();
  const vLabels = valuationLabels(language).vehicleInfo;
  const conditionLabels = (vLabels as { conditions?: Record<string, string> })
    .conditions || {
    excellent: "Mycket bra skick",
    good: "Bra skick",
    normal: "Normalt skick",
    poor: "Slitet skick",
    renovation: "Renoveringsobjekt",
  };
  const isFormValid = (condition || !showCondition) && mileage && keys;
  const [mileageError, setMileageError] = useState("");
  const [horsePowerError, setHorsePowerError] = useState("");
  const [electricDriveRangeError, setElectricDriveRangeError] = useState("");
  const [keysError, setKeysError] = useState("");

  const validateMileage = (value: string) => {
    const numValue = parseInt(value);
    if (!value.trim()) return vLabels.mileageRequired;
    if (isNaN(numValue)) return vLabels.mileageInvalid;
    if (numValue < 0) return vLabels.mileageNegative;
    if (numValue > 100000) return vLabels.mileageMax;
    return "";
  };

  const validateHorsePower = (value: string) => {
    const numValue = parseInt(value);
    if (!value.trim()) return vLabels.horsePowerRequired;
    if (isNaN(numValue)) return vLabels.horsePowerInvalid;
    if (numValue <= 0) return vLabels.horsePowerMin;
    return "";
  };
  const validateElectricDriveRange = (value: string) => {
    const numValue = parseInt(value);
    if (!value.trim()) return vLabels.electricRangeRequired;
    if (isNaN(numValue)) return vLabels.electricRangeInvalid;
    if (numValue <= 0) return vLabels.electricRangeMin;
    return "";
  };
  const handleMileageChange = (value: string) => {
    onMileageChange(value);
    setMileageError(validateMileage(value));
  };

  const handleKeysChange = (value: number) => {
    onKeysChange(value);
    setKeysError("");
  };

  const handleHorsePowerChange = (value: string) => {
    onHorsePowerChange(value);
    setHorsePowerError(validateHorsePower(value));
  };
  const handleElectricDriveRangeChange = (value: string) => {
    onElectricDriveRangeChange(value);
    setElectricDriveRangeError(validateElectricDriveRange(value));
  };

  const handleNext = () => {
    if (!keys) {
      setKeysError(vLabels.keysRequired);
      return;
    }
    if (!horsePower) {
      setHorsePowerError(vLabels.horsePowerEnter);
      return;
    }
    if (
      (fuelType === "El" || fuelType === "Elbil" || fuelType === "ELECTRIC") &&
      !electricDriveRange
    ) {
      setElectricDriveRangeError(vLabels.electricRangeEnter);
      return;
    }
    if (mileage && parseInt(mileage) <= 0) {
      setMileageError(vLabels.mileageNegative);
      return;
    }
    onNext();
  };

  return (
    <div className="space-y-4">
      {showCondition && (
        <div className="space-y-1.5">
          <h3 className="font-medium text-sm">{vLabels.conditionTitle}</h3>
          <div className="space-y-2">
            {CONDITION_KEYS.map((key) => (
              <label
                key={key}
                className="flex items-center space-x-2 cursor-pointer"
              >
                <input
                  type="radio"
                  name="condition"
                  value={key}
                  checked={
                    condition === key ||
                    condition === (conditionLabels[key] || key)
                  }
                  onChange={(e) => onConditionChange(e.target.value)}
                  className="w-4 h-4 text-accent border-gray-300 focus:ring-accent"
                />
                <span className="text-sm">{conditionLabels[key] || key}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {(condition || !showCondition) && (
        <div className="space-y-1.5">
          <h3 className="font-medium text-sm">{vLabels.mileageLabel}</h3>
          <p className="text-xs text-muted-foreground mb-2">
            {vLabels.mileagePlaceholder}
          </p>
          <Input
            type="number"
            placeholder={vLabels.mileagePlaceholder}
            value={mileage || ""}
            onChange={(e) => handleMileageChange(e.target.value)}
            className={`h-9 ${mileageError ? "border-red-500" : ""}`}
            min="0"
            max="100000"
          />
          {mileageError && (
            <p className="text-red-500 text-xs">{mileageError}</p>
          )}
        </div>
      )}

      {(condition || !showCondition) && (
        <div className="space-y-1.5">
          <h3 className="font-medium text-sm">{vLabels.keysLabel}</h3>
          <div className="flex gap-2">
            {[1, 2, 3].map((num) => (
              <Button
                key={num}
                type="button"
                variant="formOption"
                className={
                  keys === num
                    ? "border-accent border-2 bg-accent/20"
                    : "border-gray-400"
                }
                onClick={() => handleKeysChange(num)}
              >
                {num === 3 ? "3+" : num}
              </Button>
            ))}
          </div>
          {keysError && <p className="text-red-500 text-xs">{keysError}</p>}
        </div>
      )}

      {/* Hästkrafter - shown directly after condition */}
      {(condition || !showCondition) && (
        <div className="space-y-1.5">
          <h3 className="font-medium text-sm">{vLabels.horsePowerLabel}</h3>
          <Input
            type="number"
            value={horsePower || ""}
            onChange={(e) => handleHorsePowerChange(e.target.value)}
            placeholder={vLabels.horsePowerPlaceholder}
            className="h-9"
          />
          {horsePowerError && (
            <p className="text-red-500 text-xs">{horsePowerError}</p>
          )}
        </div>
      )}

      {/* Räckvidd (WLTP) - only shown for electric vehicles */}
      {(condition || !showCondition) &&
        (fuelType === "El" ||
          fuelType === "Elbil" ||
          fuelType === "ELECTRIC") && (
          <div className="space-y-1.5">
            <h3 className="font-medium text-sm">
              {vLabels.electricRangeLabel}
            </h3>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={electricDriveRange || ""}
                onChange={(e) => handleElectricDriveRangeChange(e.target.value)}
                placeholder={vLabels.electricRangePlaceholder}
                className="h-9"
              />
              <span className="text-sm text-muted-foreground">km</span>
            </div>
            {electricDriveRangeError && (
              <p className="text-red-500 text-xs">{electricDriveRangeError}</p>
            )}
          </div>
        )}

      <Button className="w-full" onClick={handleNext} disabled={!isFormValid}>
        {vLabels.next}
      </Button>
    </div>
  );
};
